import { useEffect, useState } from "react";
import { api, money, printifyOrderBadge, printifyOrderLabel } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, PackageCheck, RefreshCw, Truck, ExternalLink, Zap, CheckCircle2 } from "lucide-react";
import { Badge } from "@/components/ui/badge";

export default function StoreOrders() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [status, setStatus] = useState({ configured: false, webhooks: 0 });
  const [enabling, setEnabling] = useState(false);

  const load = () => Promise.all([
    api.get("/orders?order_type=printify").then(({ data }) => setOrders(data)),
    api.get("/printify/status").then(({ data }) => setStatus(data)),
  ]).finally(() => setLoading(false));

  useEffect(() => { load(); }, []);

  const sync = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post("/printify/sync-orders");
      toast.success(`Synced ${data.synced} order${data.synced === 1 ? "" : "s"} from Printify`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Sync failed"); }
    finally { setSyncing(false); }
  };

  const enableRealtime = async () => {
    setEnabling(true);
    try {
      const { data } = await api.post("/printify/register-webhooks", { public_base: window.location.origin });
      toast.success(`Real-time updates enabled (${data.registered.length} events)`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not enable real-time updates"); }
    finally { setEnabling(false); }
  };

  if (loading) return <div className="flex justify-center py-24"><Loader2 className="w-6 h-6 animate-spin text-blue-600" /></div>;

  return (
    <div className="max-w-[1200px] mx-auto" data-testid="store-orders-page">
      <div className="mb-5 flex items-end justify-between flex-wrap gap-3">
        <div>
          <p className="overline text-orange-600">Printify · Fulfillment</p>
          <h1 className="font-display text-3xl font-bold tracking-tight mt-1">Store orders</h1>
          <p className="text-sm text-zinc-500 mt-1">Live production &amp; shipping status for orders fulfilled by Printify — synced into Heartline.</p>
        </div>
        <div className="flex items-center gap-2">
          {status.webhooks > 0 ? (
            <span className="h-10 px-3 rounded-md bg-emerald-50 border border-emerald-200 text-emerald-700 text-sm font-medium flex items-center gap-1.5" data-testid="realtime-on"><CheckCircle2 className="w-4 h-4" /> Real-time on</span>
          ) : (
            <button onClick={enableRealtime} disabled={enabling || !status.configured} data-testid="enable-realtime-btn" className="h-10 px-3.5 rounded-md border border-zinc-200 hover:bg-zinc-50 text-zinc-700 text-sm font-medium flex items-center gap-1.5 disabled:opacity-50">
              {enabling ? <Loader2 className="w-4 h-4 animate-spin" /> : <Zap className="w-4 h-4" />} Enable real-time
            </button>
          )}
          <button onClick={sync} disabled={syncing || !status.configured} data-testid="sync-orders-btn" className="h-10 px-4 rounded-md bg-zinc-900 hover:bg-zinc-800 text-white text-sm font-medium flex items-center gap-1.5 disabled:opacity-50">
            {syncing ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />} Sync from Printify
          </button>
        </div>
      </div>

      {orders.length === 0 ? (
        <div className="border border-dashed border-zinc-300 rounded-lg p-16 text-center" data-testid="no-store-orders">
          <PackageCheck className="w-10 h-10 text-zinc-300 mx-auto" />
          <p className="text-sm text-zinc-600 mt-3 font-medium">No store orders yet</p>
          <p className="text-xs text-zinc-400 mt-1 max-w-sm mx-auto">When a product sells on Shopify, Printify fulfills it and the order appears here. Click "Sync from Printify" to pull the latest, or "Enable real-time" for automatic updates.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {orders.map((o) => (
            <div key={o.id} className="bg-white border border-zinc-200 rounded-lg p-4" data-testid={`store-order-${o.id}`}>
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-mono text-zinc-400">#{o.printify_order_id}</span>
                    <Badge className={`text-[9px] border ${printifyOrderBadge[o.status] || printifyOrderBadge.pending}`}>{printifyOrderLabel(o.status)}</Badge>
                  </div>
                  <div className="text-sm font-semibold mt-1">{o.buyer_name}</div>
                  <div className="text-xs text-zinc-500 mt-0.5">{(o.line_items || []).map((li) => `${li.qty}× ${li.product_title}`).join(" · ")}</div>
                </div>
                <div className="text-right">
                  <div className="text-base font-bold">{money(o.subtotal)}</div>
                  <div className="text-[11px] text-zinc-400">{o.units_total} unit{o.units_total === 1 ? "" : "s"}</div>
                </div>
              </div>
              {o.tracking?.number && (
                <div className="mt-3 pt-3 border-t border-zinc-100 flex items-center gap-2 text-sm">
                  <Truck className="w-4 h-4 text-emerald-600" />
                  <span className="text-zinc-600">{o.tracking.carrier} · {o.tracking.number}</span>
                  {o.tracking.url && <a href={o.tracking.url} target="_blank" rel="noreferrer" data-testid={`track-${o.id}`} className="text-blue-600 hover:text-blue-700 inline-flex items-center gap-1 text-xs font-medium">Track <ExternalLink className="w-3 h-3" /></a>}
                  {o.tracking.delivered_at && <span className="text-emerald-600 text-xs font-medium ml-auto">Delivered</span>}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
